import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, GraduationCap, BookOpen, Monitor, Building2, UserCheck, Link2, Search, KeyRound, MapPin, CalendarDays, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import CategoryMaster from './CategoryMaster';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../../components/ui/badge';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ─── Reusable Simple Master CRUD ─────────────────────────
function SimpleMaster({ title, icon: Icon, endpoint, dataKey, nameLabel = 'Name' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);

  const fetch = useCallback(() => {
    setLoading(true);
    api.get(endpoint).then(r => setItems(r.data[dataKey] || [])).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [endpoint, dataKey]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!name.trim()) return toast.error(`${nameLabel} is required`);
    try {
      if (editItem) {
        await api.put(`${endpoint}/${editItem.id}`, { name: name.trim() });
        toast.success('Updated successfully');
      } else {
        await api.post(endpoint, { name: name.trim() });
        toast.success('Created successfully');
      }
      setOpen(false); setEditItem(null); setName(''); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${endpoint}/${deleteItem.id}`);
      toast.success('Deleted'); setDeleteItem(null); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <span className="text-xs text-slate-400 ml-1">({items.length})</span>
        </div>
        <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs" onClick={() => { setEditItem(null); setName(''); setOpen(true); }} data-testid={`add-${dataKey}`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500"></div></div>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">No {title.toLowerCase()} added yet</p>
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead className="text-[10px] uppercase">{nameLabel}</TableHead><TableHead className="w-24 text-[10px] uppercase">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                <TableCell className="text-sm font-medium">{item.name}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditItem(item); setName(item.name); setOpen(true); }} data-testid={`edit-${item.id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteItem(item)} data-testid={`delete-${item.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editItem ? `Edit ${title}` : `Add ${title}`}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>{nameLabel}</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder={`Enter ${nameLabel.toLowerCase()}`} data-testid={`input-${dataKey}-name`} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid={`save-${dataKey}`}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {title}?</AlertDialogTitle><AlertDialogDescription>This will permanently delete "{deleteItem?.name}". This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── COUNSELLOR MASTER (with login credentials) ─────────────
function CounsellorMaster() {
  const [items, setItems] = useState([]);
  const [logins, setLogins] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState('');
  const [deleteItem, setDeleteItem] = useState(null);
  const [loginDialog, setLoginDialog] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [saving, setSaving] = useState(false);

  const portalUrl = `${window.location.origin}/login`;

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get('/training/counsellors'),
      api.get('/training/demos/counsellor-logins/list'),
    ]).then(([cR, lR]) => {
      if (cR.status === 'fulfilled') setItems(cR.value.data.counsellors || []);
      if (lR.status === 'fulfilled') {
        const map = {};
        (lR.value.data.items || []).forEach(l => { if (l.has_login) map[l.counsellor_id] = l; });
        setLogins(map);
      }
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!name.trim()) return toast.error('Counsellor name is required');
    try {
      if (editItem) { await api.put(`/training/counsellors/${editItem.id}`, { name: name.trim() }); toast.success('Counsellor updated'); }
      else { await api.post('/training/counsellors', { name: name.trim() }); toast.success('Counsellor created'); }
      setOpen(false); setEditItem(null); setName(''); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/training/counsellors/${deleteItem.id}`); toast.success('Deleted'); setDeleteItem(null); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const openLoginDialog = (c) => {
    const existing = logins[c.id];
    setLoginDialog({ counsellor: c, isEdit: !!existing });
    setLoginForm({ email: existing?.email || '', password: '' });
  };

  const handleSaveLogin = async () => {
    const { counsellor, isEdit } = loginDialog;
    if (!loginForm.email.trim()) return toast.error('Email (username) is required');
    if (!isEdit && !loginForm.password) return toast.error('Password is required');
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { email: loginForm.email.trim().toLowerCase() };
        if (loginForm.password) payload.password = loginForm.password;
        await api.put(`/training/demos/counsellor-logins/${logins[counsellor.id].user_id}`, payload);
        toast.success('Login updated');
      } else {
        await api.post('/training/demos/counsellor-logins', {
          counsellor_id: counsellor.id,
          email: loginForm.email.trim().toLowerCase(),
          password: loginForm.password,
        });
        toast.success('Login created');
      }
      setLoginDialog(null); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const toggleLoginActive = async (c) => {
    const l = logins[c.id];
    try { await api.put(`/training/demos/counsellor-logins/${l.user_id}`, { status: l.status === 'active' ? 'inactive' : 'active' }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const removeLogin = async (c) => {
    try { await api.delete(`/training/demos/counsellor-logins/${logins[c.id].user_id}`); toast.success('Login removed'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-sm flex items-center justify-between" data-testid="counsellor-portal-url">
        <div>
          <p className="text-[10px] uppercase text-indigo-500 font-semibold tracking-wider mb-0.5">Counsellor Login Portal</p>
          <a href={portalUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 font-mono hover:underline">{portalUrl}</a>
          <p className="text-[10px] text-indigo-400 mt-0.5">Counsellors sign in here with the email + password set below and land on Trainer Demo.</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[10px] border-indigo-200 text-indigo-600" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success('URL copied!'); }} data-testid="copy-counsellor-portal-url">Copy Link</Button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">Counsellors</h3>
          <span className="text-xs text-slate-400 ml-1">({Object.keys(logins).length} with login / {items.length} total)</span>
        </div>
        <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs" onClick={() => { setEditItem(null); setName(''); setOpen(true); }} data-testid="add-counsellors">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Counsellor
        </Button>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500"></div></div>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">No counsellors added yet</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] uppercase">Counsellor Name</TableHead>
            <TableHead className="text-[10px] uppercase">Username (email)</TableHead>
            <TableHead className="text-[10px] uppercase text-center">Login</TableHead>
            <TableHead className="w-24 text-[10px] uppercase">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(c => {
              const login = logins[c.id];
              return (
                <TableRow key={c.id}>
                  <TableCell className="text-sm font-medium">{c.name}</TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{login?.email || <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-center">
                    {login ? (
                      <div className="flex items-center justify-center gap-1">
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 cursor-pointer ${login.status === 'active' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-500 border-red-200'}`}
                          onClick={() => toggleLoginActive(c)} data-testid={`counsellor-login-status-${c.id}`}>
                          {login.status === 'active' ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <button onClick={() => openLoginDialog(c)} className="text-[10px] text-violet-500 hover:underline" data-testid={`counsellor-reset-password-${c.id}`}>Reset</button>
                        <button onClick={() => removeLogin(c)} className="text-[10px] text-red-400 hover:underline" data-testid={`counsellor-remove-login-${c.id}`}>Remove</button>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] text-violet-600" onClick={() => openLoginDialog(c)} data-testid={`create-counsellor-login-${c.id}`}>+ Create Login</Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditItem(c); setName(c.name); setOpen(true); }} data-testid={`edit-counsellor-${c.id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteItem(c)} data-testid={`delete-counsellor-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editItem ? 'Edit Counsellor' : 'Add Counsellor'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Counsellor Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter counsellor name" data-testid="input-counsellors-name" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid="save-counsellors">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Counsellor?</AlertDialogTitle><AlertDialogDescription>This will permanently delete "{deleteItem?.name}". Their login (if any) stays until removed separately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!loginDialog} onOpenChange={() => setLoginDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="counsellor-login-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-violet-600" />
              {loginDialog?.isEdit ? 'Update Login' : 'Create Login'} — {loginDialog?.counsellor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Username (email)</Label><Input value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} placeholder="name@company.com" data-testid="counsellor-login-email" /></div>
            <div><Label>Password{loginDialog?.isEdit ? ' (leave blank to keep)' : ''}</Label><Input value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} placeholder="Set a password" data-testid="counsellor-login-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginDialog(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveLogin} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid="save-counsellor-login">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── TRAINER MASTER (with working days/hours) ─────────────
function TrainerMaster() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', available_days: [], available_times: ['10:00 AM', '8:00 PM'], status: 'active' });
  const [deleteItem, setDeleteItem] = useState(null);
  const [trainerLogins, setTrainerLogins] = useState({});
  const [loginDialog, setLoginDialog] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginSaving, setLoginSaving] = useState(false);

  const trainerPortalUrl = `${window.location.origin}/trainer/login`;

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get('/training/trainers'),
      api.get('/training/trainer-logins'),
    ]).then(([tR, lR]) => {
      if (tR.status === 'fulfilled') setTrainers(tR.value.data.trainers || []);
      if (lR.status === 'fulfilled') {
        const map = {};
        (lR.value.data.logins || []).forEach(l => { map[l.trainer_id] = l; });
        setTrainerLogins(map);
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => setForm({ name: '', available_days: [], available_times: ['10:00 AM', '8:00 PM'], status: 'active' });

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      if (editItem) {
        await api.put(`/training/trainers/${editItem.id}`, form);
        toast.success('Trainer updated');
      } else {
        await api.post('/training/trainers', form);
        toast.success('Trainer created');
      }
      setOpen(false); setEditItem(null); resetForm(); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/training/trainers/${deleteItem.id}`); toast.success('Deleted'); setDeleteItem(null); fetchAll(); } catch (err) { toast.error('Failed'); }
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day) ? prev.available_days.filter(d => d !== day) : [...prev.available_days, day]
    }));
  };

  const toggleStatus = async (trainer) => {
    const newStatus = trainer.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/training/trainers/${trainer.id}`, { status: newStatus });
      toast.success(`${trainer.name} marked ${newStatus}`);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const openEdit = (t) => {
    setEditItem(t);
    setForm({
      name: t.name,
      available_days: t.available_days || [],
      available_times: t.available_times && t.available_times.length >= 2 ? t.available_times : ['10:00 AM', '8:00 PM'],
      status: t.status || 'active',
    });
    setOpen(true);
  };

  const activeCount = trainers.filter(t => t.status === 'active').length;
  const [trainerFilter, setTrainerFilter] = useState('all');

  // Sort: active on top, then alphabetical
  const sortedTrainers = [...trainers].sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const filteredTrainers = trainerFilter === 'all' ? sortedTrainers : sortedTrainers.filter(t => trainerFilter === 'active' ? t.status === 'active' : t.status !== 'active');

  const openLoginDialog = (trainer, isEdit = false) => {
    const existing = trainerLogins[trainer.id];
    setLoginDialog({ trainer, isEdit });
    setLoginForm({
      username: existing?.username || trainer.name.toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9.]/g, ''),
      password: '',
    });
  };

  const handleSaveLogin = async () => {
    if (!loginForm.username.trim() || !loginForm.password) {
      return toast.error('Username and password are required');
    }
    setLoginSaving(true);
    try {
      const existing = trainerLogins[loginDialog.trainer.id];
      if (existing) {
        await api.put(`/training/trainer-logins/${loginDialog.trainer.id}`, {
          username: loginForm.username.trim().toLowerCase(),
          password: loginForm.password,
        });
        toast.success('Login updated');
      } else {
        await api.post('/training/trainer-logins', {
          trainer_id: loginDialog.trainer.id,
          username: loginForm.username.trim().toLowerCase(),
          password: loginForm.password,
        });
        toast.success('Login created');
      }
      setLoginDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setLoginSaving(false);
  };

  const handleToggleLoginActive = async (trainerId, currentActive) => {
    try {
      await api.put(`/training/trainer-logins/${trainerId}`, { is_active: !currentActive });
      toast.success(currentActive ? 'Login disabled' : 'Login enabled');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteLogin = async (trainerId) => {
    try {
      await api.delete(`/training/trainer-logins/${trainerId}`);
      toast.success('Login removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      {/* Trainer Portal URL */}
      <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-sm flex items-center justify-between" data-testid="trainer-portal-url">
        <div>
          <p className="text-[10px] uppercase text-violet-500 font-semibold tracking-wider mb-0.5">Trainer Login Portal</p>
          <a href={trainerPortalUrl} target="_blank" rel="noreferrer" className="text-xs text-violet-700 font-mono hover:underline">{trainerPortalUrl}</a>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[10px] border-violet-200 text-violet-600" onClick={() => { navigator.clipboard.writeText(trainerPortalUrl); toast.success('URL copied!'); }} data-testid="copy-portal-url">
          Copy Link
        </Button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">Trainers</h3>
          <span className="text-xs text-slate-400 ml-1">({activeCount} active / {trainers.length} total)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1" data-testid="trainer-status-filter">
            {[{k: 'all', l: 'All'}, {k: 'active', l: 'Active'}, {k: 'inactive', l: 'Inactive'}].map(f => (
              <button key={f.k} onClick={() => setTrainerFilter(f.k)} className={`px-2.5 py-1 text-[10px] font-medium rounded-sm border transition-colors ${trainerFilter === f.k ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'}`} data-testid={`trainer-filter-${f.k}`}>{f.l}</button>
            ))}
          </div>
          <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs" onClick={() => { setEditItem(null); resetForm(); setOpen(true); }} data-testid="add-trainers">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Trainer
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500"></div></div>
      ) : trainers.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">No trainers added yet</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] uppercase">Name</TableHead>
            <TableHead className="text-[10px] uppercase">Available Days</TableHead>
            <TableHead className="text-[10px] uppercase">Working Hours</TableHead>
            <TableHead className="text-[10px] uppercase text-center">Slot Finder</TableHead>
            <TableHead className="text-[10px] uppercase text-center">Login</TableHead>
            <TableHead className="w-24 text-[10px] uppercase">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filteredTrainers.map(t => {
              const isActive = t.status === 'active';
              const days = (t.available_days || []).map(d => d.slice(0, 3)).join(', ');
              const times = t.available_times || [];
              const hours = times.length >= 2 ? `${times[0]} - ${times[1]}` : '-';
              const login = trainerLogins[t.id];
              return (
                <TableRow key={t.id} className={isActive ? '' : 'opacity-50'}>
                  <TableCell className="text-sm font-medium">{t.name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{days || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{hours}</TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={() => toggleStatus(t)}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors cursor-pointer ${isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-slate-100 text-slate-400 border border-slate-200 hover:bg-slate-200'}`}
                      data-testid={`toggle-status-${t.id}`}
                    >
                      {isActive ? 'Active' : 'Inactive'}
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    {login ? (
                      <div className="flex items-center justify-center gap-1">
                        <Badge variant="outline" className={`text-[9px] px-1.5 py-0 cursor-pointer ${login.is_active ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-500 border-red-200'}`}
                          onClick={() => handleToggleLoginActive(t.id, login.is_active)}
                          data-testid={`login-status-${t.id}`}
                        >
                          {login.is_active ? 'Enabled' : 'Disabled'}
                        </Badge>
                        <button onClick={() => openLoginDialog(t, true)} className="text-[10px] text-violet-500 hover:underline" data-testid={`reset-password-${t.id}`}>Reset</button>
                      </div>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] text-violet-600" onClick={() => openLoginDialog(t, false)} data-testid={`create-login-${t.id}`}>
                        + Create Login
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)} data-testid={`edit-trainer-${t.id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteItem(t)} data-testid={`delete-trainer-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editItem ? 'Edit Trainer' : 'Add Trainer'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Trainer name" data-testid="input-trainer-name" /></div>
            <div>
              <Label className="mb-2 block">Available Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map(day => (
                  <button key={day} type="button" onClick={() => toggleDay(day)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${form.available_days.includes(day) ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'}`}
                    data-testid={`day-${day.toLowerCase()}`}
                  >{day.slice(0, 3)}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input value={form.available_times[0]} onChange={e => setForm(p => ({ ...p, available_times: [e.target.value, p.available_times[1]] }))} placeholder="10:00 AM" data-testid="input-start-time" />
              </div>
              <div>
                <Label>End Time</Label>
                <Input value={form.available_times[1]} onChange={e => setForm(p => ({ ...p, available_times: [p.available_times[0], e.target.value] }))} placeholder="8:00 PM" data-testid="input-end-time" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Slot Finder Status</Label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setForm(p => ({ ...p, status: 'active' }))}
                  className={`px-4 py-2 rounded-sm text-xs font-medium border transition-colors ${form.status === 'active' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid="status-active"
                >Active</button>
                <button type="button" onClick={() => setForm(p => ({ ...p, status: 'inactive' }))}
                  className={`px-4 py-2 rounded-sm text-xs font-medium border transition-colors ${form.status === 'inactive' ? 'bg-slate-600 text-white border-slate-600' : 'bg-white text-slate-600 border-slate-200'}`}
                  data-testid="status-inactive"
                >Inactive</button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid="save-trainer">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Trainer?</AlertDialogTitle><AlertDialogDescription>This will permanently delete "{deleteItem?.name}" and all their course mappings.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Trainer Login Dialog */}
      <Dialog open={!!loginDialog} onOpenChange={() => setLoginDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="trainer-login-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {loginDialog?.isEdit ? 'Reset Password' : 'Create Login'} — {loginDialog?.trainer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Username</Label>
              <Input
                value={loginForm.username}
                onChange={e => setLoginForm(p => ({ ...p, username: e.target.value }))}
                placeholder="e.g. john.doe"
                className="h-9 text-xs font-mono"
                data-testid="login-username-input"
              />
            </div>
            <div>
              <Label className="text-xs">{loginDialog?.isEdit ? 'New Password' : 'Password'}</Label>
              <Input
                type="password"
                value={loginForm.password}
                onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Min 4 characters"
                className="h-9 text-xs"
                data-testid="login-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoginDialog(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSaveLogin} disabled={loginSaving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs" data-testid="save-login-btn">
              {loginSaving ? 'Saving...' : loginDialog?.isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── TRAINER-COURSE MAPPING ───────────────────────────────
function TrainerCourseMapping() {
  const [mappings, setMappings] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [selectedCourses, setSelectedCourses] = useState([]);

  const fetch = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get('/training/trainer-course-map/enriched'),
      api.get('/training/trainers'),
      api.get('/training/courses'),
    ]).then(([mRes, tRes, cRes]) => {
      if (mRes.status === 'fulfilled') setMappings(mRes.value.data.mappings || []);
      if (tRes.status === 'fulfilled') setTrainers(tRes.value.data.trainers || []);
      if (cRes.status === 'fulfilled') setCourses(cRes.value.data.courses || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!selectedTrainer || selectedCourses.length === 0) return toast.error('Select a trainer and at least one course');
    try {
      await api.post('/training/trainer-course-map', { trainer_id: selectedTrainer, course_ids: selectedCourses });
      toast.success('Mapping saved');
      setOpen(false); setSelectedTrainer(''); setSelectedCourses([]); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleCourse = (cid) => {
    setSelectedCourses(prev => prev.includes(cid) ? prev.filter(c => c !== cid) : [...prev, cid]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">Trainer-Course Mapping</h3>
          <span className="text-xs text-slate-400 ml-1">({mappings.length} trainers mapped)</span>
        </div>
        <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs" onClick={() => { setSelectedTrainer(''); setSelectedCourses([]); setOpen(true); }} data-testid="add-mapping">
          <Plus className="w-3.5 h-3.5 mr-1" /> Map Trainer
        </Button>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500"></div></div>
      ) : mappings.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">No mappings created yet</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] uppercase">Trainer</TableHead>
            <TableHead className="text-[10px] uppercase">Courses</TableHead>
            <TableHead className="w-20 text-[10px] uppercase">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {mappings.map(m => (
              <TableRow key={m.trainer_id}>
                <TableCell className="text-sm font-medium">{m.trainer_name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {m.courses.map(c => (
                      <span key={c.course_id} className="inline-block px-2 py-0.5 bg-violet-50 text-violet-700 text-xs rounded-sm border border-violet-100">{c.course_name}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                    setSelectedTrainer(m.trainer_id);
                    setSelectedCourses(m.courses.map(c => c.course_id));
                    setOpen(true);
                  }} data-testid={`edit-mapping-${m.trainer_id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Map Trainer to Courses</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Trainer</Label>
              <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
                <SelectTrigger data-testid="select-trainer"><SelectValue placeholder="Select trainer" /></SelectTrigger>
                <SelectContent>
                  {trainers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Courses</Label>
              {courses.length === 0 ? (
                <p className="text-xs text-slate-400">No courses available. Add courses first.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-sm p-3">
                  {courses.map(c => (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={selectedCourses.includes(c.id)} onCheckedChange={() => toggleCourse(c.id)} data-testid={`course-check-${c.id}`} />
                      <span className="text-sm">{c.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid="save-mapping">Save Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── VENUE LABS MASTER ────────────────────────────────────
function VenueLabsMaster() {
  const [labs, setLabs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ lab_number: '', location_id: '', capacity: '', notes: '' });
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get('/venue/admin/labs'),
      api.get('/training/centres'),
    ]).then(([lR, cR]) => {
      if (lR.status === 'fulfilled') setLabs(lR.value.data.labs || []);
      if (cR.status === 'fulfilled') setLocations(cR.value.data.centres || []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => setForm({ lab_number: '', location_id: '', capacity: '', notes: '' });

  const handleSave = async () => {
    if (!form.lab_number.trim()) return toast.error('Lab number is required');
    if (!form.location_id) return toast.error('Location is required');
    const payload = {
      lab_number: form.lab_number.trim(),
      location_id: form.location_id,
      capacity: Number(form.capacity || 0),
      notes: form.notes.trim(),
    };
    try {
      if (editItem) {
        await api.put(`/venue/admin/labs/${editItem.id}`, payload);
        toast.success('Lab updated');
      } else {
        await api.post('/venue/admin/labs', payload);
        toast.success('Lab created');
      }
      setOpen(false); setEditItem(null); resetForm(); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/venue/admin/labs/${deleteItem.id}`); toast.success('Deleted'); setDeleteItem(null); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-slate-900">Venue Labs</h3>
          <span className="text-xs text-slate-400 ml-1">({labs.length})</span>
        </div>
        <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs" onClick={() => { setEditItem(null); resetForm(); setOpen(true); }} data-testid="add-venue-lab">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Lab
        </Button>
      </div>

      <p className="text-[11px] text-slate-500 mb-3">Labs shown here appear in the public Venue Requirement form dropdown.</p>

      {loading ? (
        <div className="h-20 flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-violet-500"></div></div>
      ) : labs.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-6">No labs added yet</p>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] uppercase">Lab Number</TableHead>
            <TableHead className="text-[10px] uppercase">Location</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Capacity</TableHead>
            <TableHead className="text-[10px] uppercase">Notes</TableHead>
            <TableHead className="w-24 text-[10px] uppercase">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {labs.map(l => (
              <TableRow key={l.id}>
                <TableCell className="text-sm font-medium">{l.lab_number}</TableCell>
                <TableCell className="text-sm text-slate-600">{l.location_name}</TableCell>
                <TableCell className="text-sm text-right">{l.capacity || '—'}</TableCell>
                <TableCell className="text-xs text-slate-500">{l.notes || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                      setEditItem(l);
                      setForm({ lab_number: l.lab_number, location_id: l.location_id, capacity: String(l.capacity || ''), notes: l.notes || '' });
                      setOpen(true);
                    }} data-testid={`edit-lab-${l.id}`}><Pencil className="w-3.5 h-3.5 text-slate-400" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteItem(l)} data-testid={`delete-lab-${l.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editItem ? 'Edit Lab' : 'Add Lab'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Lab Number *</Label><Input value={form.lab_number} onChange={e => setForm(p => ({ ...p, lab_number: e.target.value }))} placeholder="e.g. Lab-01 or L-204" data-testid="input-lab-number" /></div>
            <div>
              <Label>Location *</Label>
              <Select value={form.location_id} onValueChange={v => setForm(p => ({ ...p, location_id: v }))}>
                <SelectTrigger data-testid="select-lab-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {locations.length === 0 && <p className="text-[11px] text-amber-600 mt-1">Add centres in the Centres tab first.</p>}
            </div>
            <div><Label>Capacity</Label><Input type="number" min="0" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))} placeholder="e.g. 30" data-testid="input-lab-capacity" /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" data-testid="input-lab-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm" data-testid="save-lab">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Lab?</AlertDialogTitle><AlertDialogDescription>This will permanently delete lab &quot;{deleteItem?.lab_number}&quot;. Existing venue requirements referencing this lab will keep their historical data.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── HOLIDAYS MASTER ─────────────────────────────────────
function HolidayMaster() {
  const [items, setItems] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [form, setForm] = useState({ date: '', name: '', scope: 'all', centre_ids: [], recurring: false });
  const [calOpen, setCalOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const fetch = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/training/holidays'),
      api.get('/training/centres'),
    ]).then(([h, c]) => { setItems(h.data.holidays || []); setCentres(c.data.centres || []); })
      .catch(() => toast.error('Failed to load holidays'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => { setEdit(null); setForm({ date: '', name: '', scope: 'all', centre_ids: [], recurring: false }); setOpen(true); };
  const openEdit = (h) => { setEdit(h); setForm({ date: h.date, name: h.name, scope: h.scope || 'all', centre_ids: h.centre_ids || [], recurring: !!h.recurring }); setOpen(true); };

  const save = async () => {
    if (!form.date || !form.name.trim()) return toast.error('Date and name are required');
    if (form.scope === 'centre' && form.centre_ids.length === 0) return toast.error('Select at least one centre');
    try {
      if (edit) { await api.put(`/training/holidays/${edit.id}`, form); toast.success('Holiday updated'); }
      else { await api.post('/training/holidays', form); toast.success('Holiday added'); }
      setOpen(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async () => {
    try { await api.delete(`/training/holidays/${deleteItem.id}`); toast.success('Deleted'); setDeleteItem(null); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };
  const centreName = (id) => centres.find((c) => c.id === id)?.name || id;
  const toggleCentre = (id) => setForm((p) => ({ ...p, centre_ids: p.centre_ids.includes(id) ? p.centre_ids.filter((x) => x !== id) : [...p.centre_ids, id] }));

  // ── Calendar view helpers ──
  const pad = (n) => String(n).padStart(2, '0');
  const monthLabel = calMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const shiftMonth = (delta) => setCalMonth((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));
  const calMap = useMemo(() => {
    const y = calMonth.getFullYear(), m = calMonth.getMonth() + 1, map = {};
    items.forEach((h) => {
      if (!h.date) return;
      const [hy, hm, hd] = h.date.split('-').map(Number);
      let key = null;
      if (h.recurring) { if (hm === m) key = `${y}-${pad(m)}-${pad(hd)}`; }
      else if (hy === y && hm === m) key = h.date;
      if (key) (map[key] = map[key] || []).push(h);
    });
    return map;
  }, [items, calMonth]); // eslint-disable-line
  const startDow = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayIso = new Date().toISOString().slice(0, 10);
  const monthHolidays = Object.entries(calMap).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div data-testid="holidays-master">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-violet-600" /><h3 className="font-semibold text-slate-800">Holidays</h3>
          <span className="text-xs text-slate-400">— public holidays are excluded from Trainer Audit &amp; Scorecard</span></div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setCalOpen(true)} data-testid="holiday-calendar-btn"><CalendarDays className="w-4 h-4 mr-1.5" />Calendar view</Button>
          <Button size="sm" onClick={openAdd} className="bg-violet-600 hover:bg-violet-700" data-testid="add-holiday-btn"><Plus className="w-4 h-4 mr-1.5" />Add Holiday</Button>
        </div>
      </div>
      {loading ? <p className="text-sm text-slate-400 py-8 text-center">Loading…</p> : (
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Applies to</TableHead><TableHead>Repeats yearly</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((h) => (
              <TableRow key={h.id} data-testid={`holiday-row-${h.id}`}>
                <TableCell className="font-medium">{h.date}</TableCell>
                <TableCell>{h.name}</TableCell>
                <TableCell>{h.scope === 'centre' ? <span className="text-[12px]">{(h.centre_ids || []).map(centreName).join(', ') || '—'}</span> : <Badge className="bg-violet-100 text-violet-700 border-0">All centres</Badge>}</TableCell>
                <TableCell>{h.recurring ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Yearly</Badge> : <span className="text-slate-400 text-xs">One-off</span>}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(h)} data-testid={`edit-holiday-${h.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => setDeleteItem(h)} data-testid={`delete-holiday-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No holidays added yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader><DialogTitle>{edit ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px]">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="mt-1" data-testid="holiday-date" /></div>
            <div><Label className="text-[12px]">Name</Label><Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Independence Day" className="mt-1" data-testid="holiday-name" /></div>
            <div><Label className="text-[12px]">Applies to</Label>
              <Select value={form.scope} onValueChange={(v) => setForm((p) => ({ ...p, scope: v }))}>
                <SelectTrigger className="mt-1" data-testid="holiday-scope"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All centres (org-wide)</SelectItem><SelectItem value="centre">Specific centre(s)</SelectItem></SelectContent>
              </Select>
            </div>
            {form.scope === 'centre' && (
              <div className="border border-slate-200 rounded-md p-2 max-h-40 overflow-y-auto space-y-1" data-testid="holiday-centres">
                {centres.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-[13px] cursor-pointer">
                    <Checkbox checked={form.centre_ids.includes(c.id)} onCheckedChange={() => toggleCentre(c.id)} />{c.name}
                  </label>
                ))}
                {centres.length === 0 && <p className="text-xs text-slate-400">No centres found.</p>}
              </div>
            )}
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <Checkbox checked={form.recurring} onCheckedChange={(v) => setForm((p) => ({ ...p, recurring: !!v }))} data-testid="holiday-recurring" />
              Repeats every year on this day
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-violet-600 hover:bg-violet-700" data-testid="holiday-save">{edit ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete holiday?</AlertDialogTitle><AlertDialogDescription>This holiday will be removed and audits for that day will show again.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={calOpen} onOpenChange={setCalOpen}>
        <DialogContent className="max-w-lg" data-testid="holiday-calendar-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-violet-600" />Holiday Calendar</DialogTitle></DialogHeader>
          <div className="flex items-center justify-between">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => shiftMonth(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-semibold text-slate-700" data-testid="cal-month-label">{monthLabel}</span>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => shiftMonth(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1 mt-2 text-center">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="text-[10px] font-semibold text-slate-400 py-1">{d}</div>)}
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} />;
              const iso = `${calMonth.getFullYear()}-${pad(calMonth.getMonth() + 1)}-${pad(day)}`;
              const hs = calMap[iso];
              const isToday = iso === todayIso;
              return (
                <div key={iso} title={hs ? hs.map((h) => h.name).join(', ') : ''}
                  data-testid={hs ? `cal-holiday-${iso}` : `cal-day-${iso}`}
                  className={`aspect-square flex items-center justify-center rounded-md text-[12px] ${hs ? 'bg-violet-600 text-white font-semibold' : 'text-slate-600'} ${isToday && !hs ? 'ring-1 ring-violet-400' : ''}`}>
                  {day}
                </div>
              );
            })}
          </div>
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="text-[11px] font-semibold text-slate-500 mb-1.5">Holidays this month</p>
            {monthHolidays.length === 0 ? <p className="text-[12px] text-slate-400">No holidays in {monthLabel}.</p> : (
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {monthHolidays.map(([iso, hs]) => hs.map((h, j) => (
                  <li key={`${iso}-${j}`} className="flex items-center gap-2 text-[12px]">
                    <span className="w-2 h-2 rounded-full bg-violet-600 shrink-0" />
                    <span className="font-medium text-slate-700 w-24 shrink-0">{new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                    <span className="text-slate-600">{h.name}</span>
                    {h.recurring && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">yearly</Badge>}
                    {h.scope === 'centre' && <Badge className="bg-slate-100 text-slate-600 border-0 text-[9px]">centre</Badge>}
                  </li>
                )))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── MASTERS PAGE ─────────────────────────────────────────
export default function MastersPage() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') || 'trainers';
  const setTab = (v) => setSp(v === 'trainers' ? {} : { tab: v }, { replace: true });
  return (
    <div className="space-y-8" data-testid="masters-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Masters</h1>
        <p className="text-sm text-slate-400 mt-1">Manage core training data</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="border-b border-border bg-transparent p-0 h-auto rounded-none w-full justify-start gap-0">
          {[
            { value: 'trainers', label: 'Trainers', icon: GraduationCap },
            { value: 'courses', label: 'Courses', icon: BookOpen },
            { value: 'course-modes', label: 'Course Modes', icon: Monitor },
            { value: 'centres', label: 'Centres', icon: Building2 },
            { value: 'venue-labs', label: 'Venue Labs', icon: MapPin },
            { value: 'counsellors', label: 'Counsellors', icon: UserCheck },
            { value: 'holidays', label: 'Holidays', icon: CalendarDays },
            { value: 'mapping', label: 'Trainer-Course Map', icon: Link2 },
            { value: 'categories', label: 'Technology', icon: Layers },
          ].map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} data-testid={`tab-${tab.value}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-600 data-[state=active]:text-violet-700 data-[state=active]:shadow-none px-4 py-2.5 text-xs font-medium"
            >
              <tab.icon className="w-3.5 h-3.5 mr-1.5" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-6">
          <TabsContent value="trainers"><Card className="p-6 border border-border"><TrainerMaster /></Card></TabsContent>
          <TabsContent value="courses"><Card className="p-6 border border-border"><SimpleMaster title="Course" icon={BookOpen} endpoint="/training/courses" dataKey="courses" nameLabel="Course Name" /></Card></TabsContent>
          <TabsContent value="course-modes"><Card className="p-6 border border-border"><SimpleMaster title="Course Mode" icon={Monitor} endpoint="/training/course-modes" dataKey="course_modes" nameLabel="Mode (e.g. Online, Offline, Hybrid)" /></Card></TabsContent>
          <TabsContent value="centres"><Card className="p-6 border border-border"><SimpleMaster title="Centre" icon={Building2} endpoint="/training/centres" dataKey="centres" nameLabel="Centre Name (Sector)" /></Card></TabsContent>
          <TabsContent value="venue-labs"><Card className="p-6 border border-border"><VenueLabsMaster /></Card></TabsContent>
          <TabsContent value="counsellors"><Card className="p-6 border border-border"><CounsellorMaster /></Card></TabsContent>
          <TabsContent value="holidays"><Card className="p-6 border border-border"><HolidayMaster /></Card></TabsContent>
          <TabsContent value="mapping"><Card className="p-6 border border-border"><TrainerCourseMapping /></Card></TabsContent>
          <TabsContent value="categories"><Card className="p-6 border border-border"><CategoryMaster isAdmin={(user?.role || '') === 'admin'} /></Card></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
